import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { COUNTRIES, DEFAULT_COUNTRY, type Country, type CountryCode } from "./catalog";
import { useRouterState } from "@tanstack/react-router";
import { parsePath } from "@/lib/i18n/locale";

const COUNTRY_COOKIE = "banana_country";
const LANG_COOKIE = "banana_lang";

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}
function writeCookie(name: string, value: string) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
}

type Ctx = {
  country: Country;
  setCountry: (code: CountryCode) => void;
  language: string;
  setLanguage: (lang: string) => void;
};

const CurrencyCtx = createContext<Ctx>({
  country: DEFAULT_COUNTRY,
  setCountry: () => {},
  language: "en",
  setLanguage: () => {},
});

/**
 * Provider hydrates AFTER first paint — so initial SSR / static render
 * always shows USD instantly; the saved selection slots in on mount.
 */
export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [country, setCountryState] = useState<Country>(DEFAULT_COUNTRY);
  const [language, setLanguageState] = useState<string>("en");
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    const savedCountry = readCookie(COUNTRY_COOKIE);
    const savedLang = readCookie(LANG_COOKIE);
    const browserLang =
      typeof navigator !== "undefined" && navigator.language
        ? navigator.language.split("-")[0]
        : null;
    const urlLocale = parsePath(pathname).locale;

    if (savedCountry) {
      const c = COUNTRIES.find((x) => x.code === savedCountry);
      if (c) setCountryState(c);
    } else if (urlLocale === "vi" || browserLang === "vi") {
      // Auto-default to Vietnam (VND) for Vietnamese visitors with no saved country.
      const vn = COUNTRIES.find((x) => x.code === "VN");
      if (vn) {
        setCountryState(vn);
        writeCookie(COUNTRY_COOKIE, "VN");
      }
    }

    if (savedLang) {
      setLanguageState(savedLang);
    } else if (browserLang) {
      setLanguageState(browserLang);
    }
  }, []);

  // URL locale is the source of truth — keep displayed language in sync with the URL.
  useEffect(() => {
    const { locale } = parsePath(pathname);
    setLanguageState((prev) => (prev === locale ? prev : locale));
    writeCookie(LANG_COOKIE, locale);
  }, [pathname]);

  const value = useMemo<Ctx>(() => ({
    country,
    setCountry: (code: CountryCode) => {
      const c = COUNTRIES.find((x) => x.code === code);
      if (!c) return;
      setCountryState(c);
      writeCookie(COUNTRY_COOKIE, code);
    },
    language,
    setLanguage: (lang: string) => {
      setLanguageState(lang);
      writeCookie(LANG_COOKIE, lang);
    },
  }), [country, language]);

  return <CurrencyCtx.Provider value={value}>{children}</CurrencyCtx.Provider>;
}

export function useCurrency() {
  return useContext(CurrencyCtx);
}